import { useSuspenseTOTPAuthenticatorsQuery } from '#api/totp-authenticators.queries.ts';
import { useSuspenseWebAuthnAuthenticatorsQuery } from '#api/webauthn-authenticators.queries.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageCloser } from '#components/patterns/page-closer/PageCloser.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { OrganizationSection } from '#screens/settings/user/organization/OrganizationSection.tsx';
import { fromArray, Titled } from '#util/title.ts';
import { isWebAuthnSupported } from '#util/webauthn-support.ts';
import { TOTPList } from './totp/list/TOTPList.tsx';
import { WebAuthnList } from './webauthn/list/WebAuthnList.tsx';

function WebAuthnSection({ totpCount }: { totpCount: number }) {
  const {
    data: { data: webAuthnData },
    isFetching,
  } = useSuspenseWebAuthnAuthenticatorsQuery();

  if (webAuthnData.length === 0) return null;

  return (
    <>
      <h2 className="h5 mb-8">Passkeys</h2>
      <WebAuthnList data={webAuthnData} isFetching={isFetching} canDelete={webAuthnData.length + totpCount > 1} />
    </>
  );
}

export function UserPage() {
  const { user } = useAuth();
  const {
    data: { data: totpData },
    isFetching: isFetchingTOTP,
  } = useSuspenseTOTPAuthenticatorsQuery();

  const webAuthnSupported = isWebAuthnSupported();

  return (
    <Titled title={(title) => fromArray(['User', title])}>
      <MainLayout
        header={
          <PageHeader title="User settings" subtitle={user?.email}>
            <PageCloser fallbackTo="/" />
          </PageHeader>
        }
      >
        <Spacer direction="vertical" size="xxwide">
          <OrganizationSection />
          <div>
            {totpData.length > 0 || webAuthnSupported ?
              <>
                {totpData.length > 0 && (
                  <>
                    <h2 className="h5 mb-8">Authenticator apps</h2>
                    <TOTPList data={totpData} isFetching={isFetchingTOTP} />
                  </>
                )}
                {webAuthnSupported && <WebAuthnSection totpCount={totpData.length} />}
                {totpData.length < 5 || webAuthnSupported ?
                  <div className="font-small flex gap-16">
                    {totpData.length < 5 && (
                      <Link to="totps/add" className="a">
                        + Add authenticator
                      </Link>
                    )}
                    {webAuthnSupported && (
                      <Link to="passkeys/add" className="a">
                        + Add passkey
                      </Link>
                    )}
                  </div>
                : <p className="p-md">Maximum number of authenticators added</p>}
              </>
            : <ErrorMessage>
                No active authenticators, please logout and back in to set up a new authenticator.
              </ErrorMessage>
            }
          </div>
        </Spacer>
      </MainLayout>
    </Titled>
  );
}
